/**
 * EEGate.tsx — Enterprise feature gate component and upsell banner.
 *
 * CR-WS-031 | TASK-UI-WS031-009
 */

import React, { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ArrowRight } from "lucide-react";
import { usePaidEnterpriseFeaturesEnabled } from "@/features/settings/components/usePaidEnterpriseFeaturesEnabled";

export function useFeatureFlag(_feature?: string): boolean {
  const isEnterprise = usePaidEnterpriseFeaturesEnabled();
  return isEnterprise;
}

export function EEUpsell({ feature = "workspace_governance" }: { feature?: string }) {
  return (
    <div
      className="ee-upsell p-8 max-w-lg mx-auto my-12 text-center border border-border-01 rounded-2xl bg-surface-01 shadow-sm"
      data-testid="ee-upsell"
      role="region"
      aria-label="Enterprise Feature Required"
    >
      <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-01/10 text-primary-01 flex items-center justify-center">
        <ShieldAlert className="w-6 h-6" />
      </div>
      <h2 className="text-xl font-bold text-text-01 mb-2">
        Enterprise Feature Required
      </h2>
      <p className="text-sm text-text-03 mb-6">
        Workspace governance and administration tools ({feature}) require an active Enterprise subscription.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link
          to="/admin/billing"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-01 text-white text-sm font-medium hover:bg-primary-01/90 transition"
        >
          <span>Upgrade to Enterprise</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to="/workspaces"
          className="px-4 py-2 rounded-lg border border-border-01 text-text-02 text-sm font-medium hover:bg-surface-02 transition"
        >
          Back to Workspaces
        </Link>
      </div>
    </div>
  );
}

export function EEGate({
  feature = "workspace_governance",
  children,
  fallback,
}: {
  feature?: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const isEE = useFeatureFlag(feature);
  if (!isEE) {
    return fallback ? <>{fallback}</> : <EEUpsell feature={feature} />;
  }
  return <>{children}</>;
}

export default EEGate;
